import React, { useEffect, useState } from "react";
import { Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import axios from "axios";

const { Option } = Select;

const IMAGES = {
  flags1: require("../assets/images/flags/us.png"),
  flags3: require("../assets/images/flags/fr.png"),
  flags4: require("../assets/images/flags/hk.png"),
  flags5: require("../assets/images/flags/ru.png"),
  flags6: require("../assets/images/flags/so.png"),
  flags7: require("../assets/images/flags/pic7.png"),
  india: require("../assets/images/flags/in.png"),
  Saudia: require("../assets/images/flags/sa.png"),
  pakistan: require("../assets/images/flags/pk.png"),
  vietnam: require("../assets/images/flags/vn.png"),
  korean: require("../assets/images/flags/kr.png"),
  myanmar: require("../assets/images/flags/mm.png"),
  Khmer: require("../assets/images/flags/km.png"),
  thailand: require("../assets/images/flags/th.png"),
  spain: require("../assets/images/flags/es.png"),
  Hmong: require("../assets/images/flags/cn.png"),
};

/* =======================
   CACHE
======================= */
const cache = {};
const pending = {};

/* =======================
   HELPERS
======================= */
const normalizeLang = (lang) => {
  if (!lang) return "en";

  if (lang === "en-US") return "en";

  return String(lang).split("-")[0];
};

const isBadTranslation = (original, translated) => {
  if (!translated || typeof translated !== "string") return true;

  const cleanOriginal = String(original || "").trim();
  const cleanTranslated = String(translated || "").trim();

  if (!cleanTranslated) return true;

  // If translated text becomes too long, API probably repeated/generated bad text
  if (
    cleanOriginal.length > 0 &&
    cleanTranslated.length > cleanOriginal.length * 4
  ) {
    return true;
  }

  // Extra guard for your specific repeated issue
  const secondScheduleCount =
    (cleanTranslated.match(/second schedule/gi) || []).length;

  if (secondScheduleCount > 3) return true;

  return false;
};

/* =======================
   API CALL
======================= */
export const translateApi = async (text, targetLang, sourceLang = "en", noteField = false) => {
  if (!text || typeof text !== "string") return text;

  const cleanText = text.trim();

  targetLang = normalizeLang(targetLang);
  sourceLang = normalizeLang(sourceLang);

  // Do not translate English to English
  if (!noteField && targetLang === "en") {
    return text;
  }

  const storedUrl = localStorage.getItem("translationUrl");
  const API_URL = "https://ebca-75-73-172-88.ngrok-free.app/translate";

  const finalUrl =
    storedUrl && storedUrl.trim() !== "" ? storedUrl.trim() : API_URL;

  try {
    const res = await axios.post(finalUrl, {
      text: cleanText,
      source: sourceLang,
      target: targetLang,
    });

    if (res?.data?.error) {
      console.warn("Translation API returned error:", res.data.error);
      return text;
    }

    const translated = res?.data?.translatedText;

    if (isBadTranslation(cleanText, translated)) {
      console.warn("Bad translation detected. Showing original text.");
      return text;
    }

    return translated;
  } catch (error) {
    console.warn("Translation failed. Showing original text.", error);
    return text;
  }
};

/* =======================
   TRANSLATE COMPONENT
======================= */
export const Translate = ({ value }) => {
  const { i18n } = useTranslation();
  const [text, setText] = useState(value || "");

  useEffect(() => {
    if (!value || typeof value !== "string") {
      setText(value || "");
      return;
    }

    const lang = normalizeLang(i18n.language || "en");

    // Reset to original value first
    setText(value);

    // Do not translate English
    if (lang === "en") {
      return;
    }

    let cancelled = false;

    const translateText = async () => {
      // Split multiline text and translate each line separately.
      // This prevents the API from repeating/generated broken paragraphs.
      const lines = value.split("\n");

      const translatedLines = await Promise.all(
        lines.map(async (line) => {
          const cleanLine = line.trim();

          // Keep empty lines
          if (!cleanLine) return "";

          const key = `${lang}_${cleanLine}`;

          if (cache[key]) {
            return cache[key];
          }

          if (!pending[key]) {
            pending[key] = translateApi(cleanLine, lang)
              .then((result) => {
                const finalResult = isBadTranslation(cleanLine, result)
                  ? cleanLine
                  : result;

                cache[key] = finalResult;
                return finalResult;
              })
              .catch(() => cleanLine)
              .finally(() => {
                delete pending[key];
              });
          }

          return pending[key];
        })
      );

      if (!cancelled) {
        setText(translatedLines.join("\n"));
      }
    };

    translateText();

    return () => {
      cancelled = true;
    };
  }, [value, i18n.language]);

  return <>{text}</>;
};

/* =======================
   LANGUAGE OPTIONS
======================= */
const selectData = [
  {
    image: IMAGES.flags1,
    title: "en",
    displayName: "English",
  },
  {
    image: IMAGES.Saudia,
    title: "ar",
    displayName: "Arabic",
  },
  {
    image: IMAGES.pakistan,
    title: "ur",
    displayName: "Urdu",
  },
  {
    image: IMAGES.flags3,
    title: "fr",
    displayName: "French",
  },
  {
    image: IMAGES.flags6,
    title: "so",
    displayName: "Soomaali",
  },
  {
    image: IMAGES.flags5,
    title: "ru",
    displayName: "Russian",
  },
  {
    image: IMAGES.india,
    title: "hi",
    displayName: "Hindi",
  },
  {
    image: IMAGES.spain,
    title: "es",
    displayName: "Spanish",
  },
  {
    image: IMAGES.vietnam,
    title: "vi",
    displayName: "Vietnamese",
  },
  {
    image: IMAGES.korean,
    title: "ko",
    displayName: "Korean",
  },
  {
    image: IMAGES.thailand,
    title: "th",
    displayName: "Thai",
  },
  {
    image: IMAGES.myanmar,
    title: "my",
    displayName: "Burmese",
  },
  {
    image: IMAGES.Hmong,
    title: "hmn",
    displayName: "Hmong",
  },
  {
    image: IMAGES.Khmer,
    title: "km",
    displayName: "Khmer",
  },
];

/* =======================
   LANGUAGE POPUP
======================= */
const LanguagePopup = ({ visible, onClose }) => {
  const { t, i18n } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  useEffect(() => {
    const storedLanguage = localStorage.getItem("language");
    const currentLanguage = normalizeLang(
      storedLanguage || i18n.language || "en"
    );

    const currentLanguageItem = selectData.find(
      (lang) => lang.title === currentLanguage
    );

    if (currentLanguageItem) {
      setSelectedLanguage(currentLanguageItem);

      if (normalizeLang(i18n.language) !== currentLanguageItem.title) {
        i18n.changeLanguage(currentLanguageItem.title).catch((error) => {
          console.error("Language change error:", error);
        });
      }
    }
  }, [i18n]);

  const handleChangeLanguage = async (value) => {
    const language = selectData.find((lang) => lang.title === value);

    if (language) {
      setSelectedLanguage(language);
      localStorage.setItem("language", language.title);

      i18n.changeLanguage(language.title).catch((error) => {
        console.error("Language change error:", error);
      });

      onClose();
    }
  };

  return (
    <Modal
      title={t("Choose a Language")}
      open={visible}
      onCancel={onClose}
      footer={null}
    >
      <Select
        value={selectedLanguage?.title}
        style={{ width: "100%" }}
        onChange={handleChangeLanguage}
      >
        {selectData.map((lang) => (
          <Option key={lang.title} value={lang.title}>
            <img
              src={lang.image}
              alt={lang.displayName}
              style={{ width: 24, marginRight: 8 }}
            />
            {lang.displayName}
          </Option>
        ))}
      </Select>
    </Modal>
  );
};

export default LanguagePopup;